import { TEAM_ROLE_LABEL, type MeResponse, type TeamSummary } from '@greenroom/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { ChevronsUpDown, Clapperboard, Film, KeyRound, LogOut, Plus, Users } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { api, errorMessage, fieldErrors } from '../lib/api'
import { meQuery, selectTeam, useCurrentTeam, useUser } from '../lib/auth'
import { Logo, LogoMark } from './logo'
import { Button } from './ui/button'
import { TokensDialog } from './tokens-dialog'
import { Dialog } from './ui/dialog'
import { Field } from './ui/field'
import { Menu, MenuItem, MenuLabel, MenuRadio, MenuSeparator } from './ui/menu'
import { EmptyState, ErrorNote } from './ui/misc'

const NAV = [
  { to: '/studios', label: 'Studios', icon: Clapperboard },
  { to: '/recordings', label: 'Recordings', icon: Film },
  { to: '/team', label: 'Team', icon: Users },
]

export function AppShell() {
  const me = useUser()
  if (me.teams.length === 0) return <NoTeam />
  return (
    <div className="flex h-full flex-col md:flex-row">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-border bg-surface md:flex">
        <div className="px-5 pt-5 pb-4">
          <Logo />
        </div>
        <div className="px-3">
          <TeamSwitcher />
        </div>
        <nav className="mt-4 flex flex-col gap-0.5 px-3">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive ? 'bg-surface-3 text-text' : 'text-muted hover:bg-surface-2 hover:text-text',
                )
              }
            >
              <Icon className="size-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto border-t border-border p-3">
          <UserMenu me={me} />
        </div>
      </aside>

      <header className="flex items-center gap-2 border-b border-border bg-surface px-4 py-2.5 md:hidden">
        <LogoMark />
        <div className="min-w-0 flex-1">
          <TeamSwitcher compact />
        </div>
        <nav className="flex items-center">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              aria-label={label}
              className={({ isActive }) => clsx('flex size-9 items-center justify-center rounded-lg', isActive ? 'bg-surface-3 text-text' : 'text-muted')}
            >
              <Icon className="size-4" />
            </NavLink>
          ))}
        </nav>
        <UserMenu me={me} compact />
      </header>

      <main className="min-h-0 min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl px-4 py-8 md:px-8 md:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  )
}

function TeamSwitcher({ compact }: { compact?: boolean }) {
  const { team, teams, setTeam } = useCurrentTeam()
  const [creating, setCreating] = useState(false)
  return (
    <>
      <Menu
        align="start"
        trigger={
          <button
            type="button"
            className={clsx(
              'flex w-full min-w-0 items-center gap-2.5 rounded-lg text-left transition-colors hover:bg-surface-2',
              compact ? 'px-2 py-1.5' : 'border border-border px-3 py-2',
            )}
          >
            <TeamAvatar team={team} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{team.name}</span>
              {!compact && <span className="block text-xs text-faint">{TEAM_ROLE_LABEL[team.role]}</span>}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 text-faint" />
          </button>
        }
      >
        <MenuLabel>Teams</MenuLabel>
        <MenuRadio value={team.id} onChange={setTeam} options={teams.map((t) => ({ value: t.id, label: t.name }))} />
        <MenuSeparator />
        <MenuItem icon={<Plus />} onSelect={() => setCreating(true)}>
          New team
        </MenuItem>
      </Menu>
      <CreateTeamDialog open={creating} onOpenChange={setCreating} onCreated={setTeam} />
    </>
  )
}

function TeamAvatar({ team }: { team: TeamSummary }) {
  return (
    <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-accent-soft text-xs font-semibold text-accent uppercase">
      {team.name.slice(0, 1)}
    </span>
  )
}

function CreateTeamDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (o: boolean) => void; onCreated: (id: string) => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const create = useMutation({
    mutationFn: (teamName: string) => api<TeamSummary>('/api/teams', { body: { name: teamName } }),
    onSuccess: (team) => {
      qc.setQueryData(meQuery.queryKey, (me: MeResponse | null | undefined) => (me ? { ...me, teams: [...me.teams, team] } : me))
      onCreated(team.id)
      onOpenChange(false)
      setName('')
    },
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (name.trim()) create.mutate(name.trim())
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="New team" description="Studios, recordings and members belong to a team.">
      <form onSubmit={submit} className="space-y-6">
        <Field label="Team name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoFocus required />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={create.isPending} disabled={!name.trim()}>
            Create team
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

function UserMenu({ me, compact }: { me: MeResponse; compact?: boolean }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [tokensOpen, setTokensOpen] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const logout = useMutation({
    mutationFn: () => api('/api/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      qc.clear()
      qc.setQueryData(meQuery.queryKey, null)
      navigate('/login', { replace: true })
    },
  })
  const initials = me.user.name
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
  return (
    <>
      <Menu
        align={compact ? 'end' : 'start'}
        trigger={
          <button type="button" className="flex min-w-0 items-center gap-2.5 rounded-lg p-1.5 text-left hover:bg-surface-2 md:w-full" aria-label="Account">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-3 text-xs font-semibold uppercase">{initials}</span>
            {!compact && (
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{me.user.name}</span>
                <span className="block truncate text-xs text-faint">{me.user.email}</span>
              </span>
            )}
          </button>
        }
      >
        <MenuLabel>{me.user.email}</MenuLabel>
        <MenuItem icon={<KeyRound />} onSelect={() => setTokensOpen(true)}>
          Access tokens
        </MenuItem>
        <MenuItem icon={<KeyRound />} onSelect={() => setChangingPassword(true)}>
          Change password
        </MenuItem>
        <MenuItem icon={<LogOut />} onSelect={() => logout.mutate()}>
          Log out
        </MenuItem>
      </Menu>
      <TokensDialog open={tokensOpen} onOpenChange={setTokensOpen} />
      <ChangePasswordDialog email={me.user.email} open={changingPassword} onOpenChange={setChangingPassword} />
    </>
  )
}

function ChangePasswordDialog({ email, open, onOpenChange }: { email: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '' })
  const change = useMutation({
    mutationFn: () => api('/api/auth/password', { body: form }),
    meta: { silent: true },
    onSuccess: () => {
      toast.success('Password changed', { description: 'Your other devices are signed out and access tokens revoked.' })
      close(false)
    },
  })
  const close = (o: boolean) => {
    onOpenChange(o)
    if (!o) {
      setForm({ currentPassword: '', newPassword: '' })
      change.reset()
    }
  }
  const errors = fieldErrors(change.error)
  const generalError = change.error && Object.keys(errors).length === 0 ? errorMessage(change.error) : null
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [key]: e.target.value })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    change.mutate()
  }
  return (
    <Dialog open={open} onOpenChange={close} title="Change password" description="Your other devices are signed out and all access tokens are revoked afterwards.">
      <form onSubmit={submit} className="space-y-4">
        <input type="email" name="username" autoComplete="username" value={email} readOnly className="hidden" />
        <Field
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={form.currentPassword}
          onChange={set('currentPassword')}
          error={errors.currentPassword}
          required
          autoFocus
        />
        <Field
          label="New password"
          type="password"
          autoComplete="new-password"
          value={form.newPassword}
          onChange={set('newPassword')}
          error={errors.newPassword}
          hint="At least 10 characters."
          minLength={10}
          required
        />
        {generalError && <ErrorNote>{generalError}</ErrorNote>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={change.isPending}>
            Change password
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

function NoTeam() {
  const [open, setOpen] = useState(false)
  return (
    <div className="flex h-full items-center justify-center px-4">
      <EmptyState
        icon={<Users />}
        title="You are not in a team any more"
        action={
          <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setOpen(true)}>
            Create a team
          </Button>
        }
      >
        Create a team to set up studios, or ask a team owner for an invite link.
      </EmptyState>
      <CreateTeamDialog open={open} onOpenChange={setOpen} onCreated={selectTeam} />
    </div>
  )
}
